import { useMemo, useState } from 'react'
import { useFiscalYear } from '@/lib/fiscalYear'
import { Link } from 'react-router-dom'
import {
  copyableSourceYears,
  getFiscalYearLabel,
  selectableFiscalYears,
  type BudgetCode,
  type NamedCodeLike,
} from '@budgetmonkey/shared'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Money,
} from '@/components/ui'
import {
  useAllBudgetCodes,
  useBudgetCodeReferenceCount,
  useBudgetCodes,
  useCreateExternalBudgetCode,
  useCreateOwnedBudgetCode,
  useRemoveBudgetCode,
  useCopyBudgetCodesToYear,
  useSetBudgetCodeActive,
} from './hooks'
import { BudgetCodeEditor, budgetCodeTitle } from './BudgetCodeEditor'
import { BudgetCodeForm, type BudgetCodeFormValues } from './BudgetCodeForm'
import { ExternalCodeForm } from './ExternalCodeForm'
import { BudgetImport } from './BudgetImport'
import { loadStateOf, LoadingOrOffline } from '@/lib/loadState'
import { humanErrorMessage } from '@/lib/errorMessage'

/** Everything the alias uniqueness check needs, from the full code set. */
function toNamedCodes(codes: readonly BudgetCode[]): NamedCodeLike[] {
  return codes.map((c) => ({
    id: c.id,
    code: c.code,
    aliases: c.aliases,
    displayName: c.displayName,
  }))
}

type Mode =
  | { kind: 'list' }
  | { kind: 'new' }
  | { kind: 'new-external' }
  | { kind: 'edit'; code: BudgetCode }
  | { kind: 'import' }

export function BudgetCodesScreen() {
  const fiscalYear = useFiscalYear()
  const codesQuery = useBudgetCodes(fiscalYear)
  const { data: codes = [], error } = codesQuery
  // Not `isLoading`: see lib/loadState — a paused query is pending with
  // nothing in flight, and would render the empty state over unasked data.
  const state = loadStateOf(codesQuery)
  const [mode, setMode] = useState<Mode>({ kind: 'list' })
  const [showInactive, setShowInactive] = useState(false)

  const create = useCreateOwnedBudgetCode(fiscalYear)
  const createExternal = useCreateExternalBudgetCode(fiscalYear)
  const setActive = useSetBudgetCodeActive(fiscalYear)

  const namedCodes = useMemo(() => toNamedCodes(codes), [codes])

  const owned = codes
    .filter((c) => !c.external)
    .filter((c) => showInactive || c.active)
    .sort((a, b) => a.code.localeCompare(b.code))
  // Deactivated external codes follow the same toggle as owned ones. They used
  // to be filtered out unconditionally, which would have made deactivating one
  // look like deleting it, with no way back to reactivate it.
  const external = codes.filter((c) => c.external).filter((c) => showInactive || c.active)
  const inactiveCount = codes.filter((c) => !c.active).length

  if (mode.kind === 'new') {
    return (
      <Section title="Add owned code" fiscalYear={fiscalYear}>
        <Card>
          <BudgetCodeForm
            existingCodes={namedCodes}
            submitLabel="Add code"
            pending={create.isPending}
            error={create.error?.message}
            onCancel={() => setMode({ kind: 'list' })}
            onSubmit={(values: BudgetCodeFormValues) =>
              create.mutate(values, { onSuccess: () => setMode({ kind: 'list' }) })
            }
          />
        </Card>
      </Section>
    )
  }

  if (mode.kind === 'new-external') {
    return (
      <Section title="Add external code" fiscalYear={fiscalYear}>
        <Card>
          <ExternalCodeForm
            existingCodes={namedCodes}
            pending={createExternal.isPending}
            error={createExternal.error?.message}
            onCancel={() => setMode({ kind: 'list' })}
            onSubmit={(values) =>
              createExternal.mutate(values, {
                onSuccess: () => setMode({ kind: 'list' }),
              })
            }
          />
        </Card>
      </Section>
    )
  }

  if (mode.kind === 'import') {
    return (
      <Section title="Import from a spreadsheet" fiscalYear={fiscalYear}>
        <BudgetImport
          fiscalYear={fiscalYear}
          existingCodes={codes}
          onDone={() => setMode({ kind: 'list' })}
        />
      </Section>
    )
  }

  // One editing mode for both kinds — `BudgetCodeEditor` decides which form a
  // code needs, so this screen cannot open the wrong one for it.
  if (mode.kind === 'edit') {
    const { code } = mode
    return (
      <Section title={`Edit ${budgetCodeTitle(code)}`} fiscalYear={fiscalYear}>
        <Card>
          <BudgetCodeEditor
            code={code}
            fiscalYear={fiscalYear}
            onDone={() => setMode({ kind: 'list' })}
          />
        </Card>
      </Section>
    )
  }

  return (
    <Section title="Budget codes" fiscalYear={fiscalYear}>
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="primary" onClick={() => setMode({ kind: 'new' })}>
          Add owned code
        </Button>
        <Button onClick={() => setMode({ kind: 'new-external' })}>
          Add external code
        </Button>
        <Button onClick={() => setMode({ kind: 'import' })}>
          Import from spreadsheet
        </Button>
        {inactiveCount > 0 && (
          <Button variant="ghost" onClick={() => setShowInactive((v) => !v)}>
            {showInactive ? 'Hide' : 'Show'} {inactiveCount} deactivated
          </Button>
        )}
        {codes.length > 0 && <CopyCodesToYear fromYear={fiscalYear} />}
      </div>

      {error !== null && <ErrorNote>{humanErrorMessage(error, "Couldn't load your budget codes.")}</ErrorNote>}

      {state !== 'ready' ? (
        <LoadingOrOffline state={state} what="Your budget codes" />
      ) : owned.length === 0 ? (
        <EmptyState title={`No budget codes for ${getFiscalYearLabel(fiscalYear)} yet`}>
          <p>
            Import them from your budget spreadsheet, add them one at a time, or
            use the{' '}
            <Link to="/setup" className="underline underline-offset-4">
              first-run setup
            </Link>
            .
          </p>
          <Button variant="primary" onClick={() => setMode({ kind: 'import' })}>
            Import from spreadsheet
          </Button>
          <CopyCodesFromYear toYear={fiscalYear} />
        </EmptyState>
      ) : (
        <ul className="space-y-3">
          {owned.map((code) => (
            <li key={code.id}>
              <CodeRow
                code={code}
                onEdit={() => setMode({ kind: 'edit', code })}
                onToggleActive={() =>
                  setActive.mutate({ id: code.id!, active: !code.active })
                }
                pending={setActive.isPending}
              />
            </li>
          ))}
        </ul>
      )}

      {external.length > 0 && (
        <section className="space-y-3 pt-4" id="external-codes">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">
            External codes ({external.length})
          </h2>
          <p className="text-sm text-slate-600">
            Colleagues' codes you file charges against. Excluded from all budget totals.
          </p>
          <ul className="space-y-3">
            {external.map((code) => (
              <li key={code.id}>
                <ExternalCodeRow
                  code={code}
                  fiscalYear={fiscalYear}
                  onEdit={() => setMode({ kind: 'edit', code })}
                  onReactivate={() =>
                    setActive.mutate({ id: code.id!, active: true })
                  }
                  onArchived={() => setShowInactive(true)}
                  reactivating={setActive.isPending}
                />
              </li>
            ))}
          </ul>
        </section>
      )}
    </Section>
  )
}

/**
 * Copy this year's codes into another year.
 *
 * §11 rules out *automatic* carryover, and this is not that — it is asked for,
 * one year at a time. Two jobs, a year apart: giving a past year the codes its
 * charges were filed against, and seeding the next year each July without
 * retyping eleven nine-segment account codes.
 *
 * A code already in the target year is skipped, matched on the account code —
 * the thing that stays the same from year to year while balances do not. So
 * running it twice is harmless, and running it after adding one code copies
 * only the one.
 */
function CopyCodesToYear({ fromYear }: { fromYear: number }) {
  const copy = useCopyBudgetCodesToYear()
  const [target, setTarget] = useState<number>()
  const [result, setResult] = useState<{ copied: number; skipped: number }>()

  const options = selectableFiscalYears().filter((year) => year !== fromYear)

  if (target === undefined) {
    return (
      <Button
        variant="ghost"
        onClick={() => {
          setResult(undefined)
          setTarget(options[0])
        }}
      >
        Copy codes to another year
      </Button>
    )
  }

  return (
    <span className="flex flex-wrap items-center gap-2">
      <span className="text-sm text-slate-600">Copy to</span>
      <select
        value={target}
        onChange={(e) => setTarget(Number(e.target.value))}
        className="min-h-11 rounded-lg border border-slate-300 bg-white px-3 text-sm"
      >
        {options.map((year) => (
          <option key={year} value={year}>
            {getFiscalYearLabel(year)}
          </option>
        ))}
      </select>
      <Button
        variant="primary"
        disabled={copy.isPending}
        onClick={() =>
          copy.mutate(
            { fromYear, toYear: target },
            { onSuccess: (r) => setResult(r) },
          )
        }
      >
        {copy.isPending ? 'Copying…' : 'Copy'}
      </Button>
      <Button variant="ghost" onClick={() => setTarget(undefined)}>
        Done
      </Button>
      {result !== undefined && (
        <span className="text-sm text-slate-600" role="status">
          {result.copied === 0
            ? 'Nothing to copy — that year already has them.'
            : `Copied ${result.copied}. Check the beginning balances there: they came across as they are, and they are the part that differs.`}
        </span>
      )}
      {copy.error !== null && <ErrorNote>{copy.error.message}</ErrorNote>}
    </span>
  )
}

/**
 * Populate an empty year by copying another year's codes into it.
 *
 * The mirror of {@link CopyCodesToYear}, placed where the need actually shows
 * up: the empty state of a year that has no codes. Matt hit this switching to
 * 25-26 to file last year's charges — last year's codes are the same as this
 * year's, only the balances differ, so retyping eleven nine-segment account
 * codes (or running first-run setup, which builds the *current* year's) is
 * exactly the work this removes. Only rendered when some other year has codes
 * to copy from; the button names the source and count so the click is not a
 * leap of faith. On success the copied codes appear and this empty state
 * unmounts — the list arriving is the confirmation.
 */
function CopyCodesFromYear({ toYear }: { toYear: number }) {
  const { data: allCodes = [] } = useAllBudgetCodes()
  const copy = useCopyBudgetCodesToYear()

  const sources = useMemo(
    () => copyableSourceYears(allCodes, toYear),
    [allCodes, toYear],
  )

  if (sources.length === 0) return null

  return (
    <div className="mt-4 space-y-2">
      <p className="text-sm text-slate-600">
        Same codes as another year? Copy them in — the account codes carry over,
        and you set this year's beginning balances after.
      </p>
      <div className="flex flex-wrap items-center gap-2">
      {sources.map(({ year, count }) => (
        <Button
          key={year}
          variant="primary"
          disabled={copy.isPending}
          onClick={() => copy.mutate({ fromYear: year, toYear })}
        >
          {copy.isPending
            ? 'Copying…'
            : `Copy ${count} code${count === 1 ? '' : 's'} from ${getFiscalYearLabel(year)}`}
        </Button>
      ))}
      </div>
      {copy.error !== null && <ErrorNote>{copy.error.message}</ErrorNote>}
    </div>
  )
}

function Section({
  title,
  fiscalYear,
  children,
}: {
  title: string
  fiscalYear: number
  children: React.ReactNode
}) {
  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        <p className="text-sm text-slate-500">{getFiscalYearLabel(fiscalYear)}</p>
      </div>
      {children}
    </div>
  )
}

function CodeRow({
  code,
  onEdit,
  onToggleActive,
  pending,
}: {
  code: BudgetCode
  onEdit: () => void
  onToggleActive: () => void
  pending: boolean
}) {
  return (
    <Card className={code.active ? '' : 'opacity-60'}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/codes/${code.id}`}
              className="font-medium text-slate-900 underline-offset-4 hover:underline"
            >
              {code.displayName}
            </Link>
            {code.fund === 'grant' && <Badge tone="ok">Grant</Badge>}
            {!code.active && <Badge>Deactivated</Badge>}
          </div>

          <p className="font-mono text-xs text-slate-500">{code.code}</p>

          {code.aliases.length > 0 && (
            <ul className="flex flex-wrap gap-1.5">
              {code.aliases.map((alias) => (
                <li
                  key={alias}
                  className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600"
                >
                  {alias}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex shrink-0 flex-col items-end gap-2">
          <Money value={code.beginningBalance} className="font-medium" />
          <div className="flex gap-1">
            <Button variant="ghost" onClick={onEdit}>
              Edit
            </Button>
            <Button variant="ghost" onClick={onToggleActive} disabled={pending}>
              {code.active ? 'Deactivate' : 'Reactivate'}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  )
}

/**
 * An external code, with the two things that can be done to it.
 *
 * Removal is one button, not a Deactivate beside a Delete, because for an
 * external code those are the same decision made once: a code with charges
 * behind it can only ever be deactivated — deleting it would orphan rows on a
 * packet already submitted — and one with none has no history worth keeping.
 * So the button names the branch it is on, and the count that decides it is
 * read from the server rather than from the fiscal year's cached transactions,
 * which would miss a reference from another year.
 *
 * Until that count arrives the button reads Deactivate. The label may only
 * promise less than it does, never more; `removeBudgetCode` re-checks anyway,
 * and reports back when it archived something this row offered to delete.
 */
function ExternalCodeRow({
  code,
  fiscalYear,
  onEdit,
  onReactivate,
  onArchived,
  reactivating,
}: {
  code: BudgetCode
  fiscalYear: number
  onEdit: () => void
  onReactivate: () => void
  onArchived: () => void
  reactivating: boolean
}) {
  const references = useBudgetCodeReferenceCount(code.id)
  const remove = useRemoveBudgetCode(fiscalYear)
  const [confirming, setConfirming] = useState(false)
  const [archivedInstead, setArchivedInstead] = useState(false)

  const referenceCount = references.data ?? 0
  const willDelete = references.data === 0

  return (
    <Card className={code.active ? '' : 'opacity-60'}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/codes/${code.id}`}
              className="font-medium text-slate-900 underline-offset-4 hover:underline"
            >
              {code.externalOwnerName}
            </Link>
            <Badge>External</Badge>
            {!code.active && <Badge>Deactivated</Badge>}
          </div>

          <p className="font-mono text-xs text-slate-500">{code.code}</p>

          {code.aliases.length > 0 && (
            <ul className="flex flex-wrap gap-1.5">
              {code.aliases.map((alias) => (
                <li
                  key={alias}
                  className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600"
                >
                  {alias}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex shrink-0 gap-1">
          <Button variant="ghost" onClick={onEdit}>
            Edit
          </Button>

          {!code.active ? (
            <Button
              variant="ghost"
              disabled={reactivating}
              onClick={() => {
                // The note explains a state the reactivation is about to undo.
                setArchivedInstead(false)
                onReactivate()
              }}
            >
              Reactivate
            </Button>
          ) : confirming ? (
            <>
              <Button
                variant="danger"
                disabled={remove.isPending}
                onClick={() =>
                  remove.mutate(code.id!, {
                    onSuccess: (outcome) => {
                      setConfirming(false)
                      if (outcome !== 'archived') return
                      // The row would otherwise vanish from a list showing only
                      // active codes — indistinguishable from the delete that
                      // was asked for and didn't happen.
                      setArchivedInstead(true)
                      onArchived()
                    },
                  })
                }
              >
                {remove.isPending ? 'Deleting…' : 'Delete for good'}
              </Button>
              <Button variant="ghost" onClick={() => setConfirming(false)}>
                Keep it
              </Button>
            </>
          ) : willDelete ? (
            <Button variant="ghost" onClick={() => setConfirming(true)}>
              Delete
            </Button>
          ) : (
            <Button
              variant="ghost"
              disabled={remove.isPending}
              onClick={() => remove.mutate(code.id!)}
            >
              {remove.isPending ? 'Deactivating…' : 'Deactivate'}
            </Button>
          )}
        </div>
      </div>

      {confirming && (
        <p className="pt-2 text-xs text-slate-600">
          Nothing is filed against this code, so it will be removed outright.
        </p>
      )}

      {!confirming && code.active && references.data !== undefined && !willDelete && (
        <p className="pt-2 text-xs text-slate-500">
          {referenceCount} {referenceCount === 1 ? 'charge is' : 'charges are'} filed
          against this code, so it can be deactivated but not deleted.
        </p>
      )}

      {archivedInstead && (
        <p className="pt-2 text-xs text-amber-700">
          Kept and deactivated — a charge was filed against it in the meantime.
        </p>
      )}

      {remove.error !== null && <ErrorNote>{remove.error.message}</ErrorNote>}
    </Card>
  )
}
